import { useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import { Col, Container, Row, Button, FormGroup } from "reactstrap";
import { ReactstrapInput, ReactstrapSelect } from "reactstrap-formik";

import * as Yup from "yup";

import FormHelpers from "../../helpers/FormHelpers";
import InfoTooltip from "../UI/Progress/InfoTooltip";
import ScrollToFieldError from "./Validation/ScrollToFieldError";
import CardPreview from '../Cards/CardPreview';

import { configuratorActions } from "../../store/configurator-slice";
import { cardPreviewActions } from "../../store/card-preview-slice";

function ReturnForm(props) {
  const dispatch = useDispatch();

  const formRef = useRef();

  // get current request state
  const currentRequest = useSelector(
    (state) => state.configurator.ConfiguratorRequest
  );
  const requestDetails = currentRequest.DonorRequest;
  const isLoggedIn = currentRequest.DonorIsLoggedIn;
  let useAddressValue = requestDetails.UseAddress;
  if (!useAddressValue) {
    useAddressValue = "billing";
  }

  // get title list
  const titles = currentRequest.Lists.DonorTitles;
  const titlesArray = titles.map((item) => {
    return item.Value;
  });

  // get country list
  const countries = currentRequest.Lists.Countries;
  const countriesArray = countries.map((item) => {
    return item.Value;
  });

  // get state list
  const states = currentRequest.Lists.States;
  const statesArray = states.map((item) => {
    return item.Value;
  });

  // get province list
  const provinces = currentRequest.Lists.Provinces;
  const provincesArray = provinces.map((item) => {
    return item.Value;
  });

  // init form validation schema
  const formSchema = Yup.object().shape({
    firstName: Yup.string().when("useAddress", (useAddress) => {
      if (useAddress === 'custom') return Yup.string().required("Required");
    }),
    lastName: Yup.string().when("useAddress", (useAddress) => {
      if (useAddress === 'custom') return Yup.string().required("Required");
    }),
    address1: Yup.string().when("useAddress", (useAddress) => {
      if (useAddress === 'custom') return Yup.string().required("Required");
    }),
    country: Yup.string().when("useAddress", (useAddress) => {
      if (useAddress === 'custom') return Yup.string().required("Required");
    }),
    city: Yup.string().when("useAddress", (useAddress) => {
      if (useAddress === 'custom') return Yup.string().required("Required");
    }),

    state: Yup.string().when(
      ["country", "useAddress"],
      (country, useAddress) => {
        if (useAddress === 'custom' && country === "USA")
          return Yup.string().required("Required");
      }
    ),

    province: Yup.string().when(
      ["country", "useAddress"],
      (country, useAddress) => {
        if (useAddress === 'custom' && country === "Canada")
          return Yup.string().required("Required");
      }
    ),

    postalCode: Yup.string().when(
      ["country", "useAddress"],
      (country, useAddress) => {
        if (
          useAddress === 'custom' &&
          (country === "USA" || country === "Canada")
        ) {
          if(country === "USA") {
            return Yup.string().required("Required").matches(/^[0-9]{5}(?:-[0-9]{4})?$/, "Must be a valid zip code");
          } else {
            return Yup.string().required("Required").matches(/^(?!.*[DFIOQU])[A-VXY][0-9][A-Z][ -]?[0-9][A-Z][0-9]$/, "Must be a valid postal code");
          }
        }
      }
    ),
  });

  const useAddressClickHandler = (e) => {
    dispatch(configuratorActions.setUseAddressValue(e.target.value));
    dispatch(cardPreviewActions.setFieldFocus(""));
  };

  const formSubmitHandler = (values) => {
    dispatch(configuratorActions.setReturn(values));
    if (values.useAddress === "none" || values.useAddress === "billing") {
      dispatch(cardPreviewActions.clearEnvelopeReturnAddress());
    }
    dispatch(cardPreviewActions.setFieldFocus(""));
    FormHelpers.scrollToTop();
  };

  const noAddressClickHandler = (values) => {
    dispatch(configuratorActions.setReturn(values));
    dispatch(cardPreviewActions.setFieldFocus(""));
  };

  const previousClickHandler = (values) => {
    dispatch(configuratorActions.setFormStep("Recipient"));
    dispatch(cardPreviewActions.setFieldFocus(""));
    FormHelpers.scrollToTop();
  };

  const inputFocusHandler = (e) => {
    dispatch(cardPreviewActions.setFieldFocus("envelope-return"));
  };

  const inputBlurHandler = (e) => {
    dispatch(cardPreviewActions.setFieldFocus(""));
  };

  const addressUpdateHandler = (e) => {
    let values = {...formRef.current.values};
    values[e.target.name] = e.target.value;

    let envelopeValues = {...values};

    if(values.country === 'USA') {
      envelopeValues.state = FormHelpers.getStateProvinceCode(states, values.state);
    } else if(values.country === 'Canada') {
      envelopeValues.province = FormHelpers.getStateProvinceCode(provinces, values.province);
    }

    dispatch(cardPreviewActions.setEnvelopeReturnAddress(envelopeValues));
  };

  return (
    <div className="cfg-input-form cfg-return" data-step="return">
      <Formik
        initialValues={{
          useAddress: useAddressValue,
          title: requestDetails.ReturnTitle,
          firstName: requestDetails.ReturnFirstName,
          lastName: requestDetails.ReturnLastName,
          address1: requestDetails.ReturnAddress1,
          address2: requestDetails.ReturnAddress2,
          country: (requestDetails.ReturnCountry) ? requestDetails.ReturnCountry : 'USA',
          state: requestDetails.ReturnStateProvince,
          province: requestDetails.ReturnStateProvince,
          city: requestDetails.ReturnCity,
          postalCode: requestDetails.ReturnZipPostalCode,
        }}
        validationSchema={formSchema}
        onSubmit={formSubmitHandler}
        innerRef={formRef}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form className="text-start">
            <ScrollToFieldError />
            <Container className="cfg-form-section">
              <Row className="mobile-only">
                <Col xs="12">
                  <CardPreview
                    zoom={0.47}
                    showCard={false}
                    showEnvelope={true}
                    preview={true}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs="12">
                  <InfoTooltip
                    id="returnHelp"
                    title="Help"
                    text="Fill in your name and address unless you are sending the card on behalf of someone else.  In that case type the name and address you want printed on the envelope."
                  />
                  <FormGroup>

                        <div className="form-check">
                          <label>
                            <Field
                              type="radio"
                              name="useAddress"
                              value="billing"
                              onClick={useAddressClickHandler}
                            />
                            Use my billing address
                          </label>
                        </div>

                    <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="useAddress"
                          value="custom"
                          onClick={useAddressClickHandler}
                        />
                        Use the name and address below
                      </label>
                    </div>
                    <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="useAddress"
                          value="none"
                          onClick={useAddressClickHandler}
                        />
                        No Return Address
                      </label>
                    </div>
                    {errors.useAddress && touched.useAddress && (
                      <div className="invalid-feedback">
                        {errors.useAddress}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              {useAddressValue !== "none" && useAddressValue !== "billing" && (
                <div>
                  <Row className="mb-2">
                    <Col xs="12">
                      <label htmlFor="title">Title:</label>
                      <Field
                        name="title"
                        component={ReactstrapSelect}
                        onChange={(e) => {
                          handleChange(e);
                          addressUpdateHandler(e);
                        }}
                        inputprops={{
                          name: "title",
                          id: "title",
                          options: titlesArray,
                          defaultOption: "Select...",
                        }}
                        onFocus={inputFocusHandler}
                        onBlur={(e) => {
                          handleBlur(e);
                          inputBlurHandler(e);
                        }}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs="12">
                        <label className="required" htmlFor="firstName">
                          First Name:
                        </label>
                        <Field
                          name="firstName"
                          id="firstName"
                          maxlength="30"
                          component={ReactstrapInput}
                          onKeyUp={addressUpdateHandler}
                          onFocus={inputFocusHandler}
                          onBlur={(e) => {
                            handleBlur(e);
                            inputBlurHandler(e);
                          }}
                        />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs="12">
                      <label className="required" htmlFor="lastName">
                        Last Name:
                      </label>
                      <Field
                        name="lastName"
                        id="lastName"
                        maxlength="30"
                        component={ReactstrapInput}
                        onKeyUp={addressUpdateHandler}
                        onFocus={inputFocusHandler}
                        onBlur={(e) => {
                          handleBlur(e);
                          inputBlurHandler(e);
                        }}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs="12">
                      <label className="required" htmlFor="address1">
                        Address 1:
                      </label>
                      <Field
                        name="address1"
                        id="address1"
                        maxlength="50"
                        component={ReactstrapInput}
                        onKeyUp={addressUpdateHandler}
                        onFocus={inputFocusHandler}
                        onBlur={(e) => {
                          handleBlur(e);
                          inputBlurHandler(e);
                        }}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs="12">
                      <label htmlFor="address2">Address 2:</label>
                      <Field
                        name="address2"
                        id="address2"
                        maxlength="50"
                        component={ReactstrapInput}
                        onKeyUp={addressUpdateHandler}
                        onFocus={inputFocusHandler}
                        onBlur={(e) => {
                          handleBlur(e);
                          inputBlurHandler(e);
                        }}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs="12">
                      <label className="required" htmlFor="country">
                        Country:
                      </label>
                      <Field
                        name="country"
                        component={ReactstrapSelect}
                        inputprops={{
                          name: "country",
                          id: "country",
                          options: countriesArray,
                          defaultOption: "Select...",
                        }}
                        onChange={(e) => {
                          handleChange(e);
                          addressUpdateHandler(e);
                        }}
                        onFocus={inputFocusHandler}
                        onBlur={(e) => {
                          handleBlur(e);
                          inputBlurHandler(e);
                        }}
                      />
                    </Col>
                  </Row>
                  {values.country === "USA" && (
                    <Row className="mb-2">
                      <Col xs="12">
                        <label
                          className={values.country === "USA" ? "required" : ""}
                          htmlFor="state"
                        >
                          State:
                        </label>
                        <Field
                          name="state"
                          component={ReactstrapSelect}
                          inputprops={{
                            name: "state",
                            id: "state",
                            options: statesArray,
                            defaultOption: "Select...",
                          }}
                          onChange={(e) => {
                            handleChange(e);
                            addressUpdateHandler(e);
                          }}
                          onFocus={inputFocusHandler}
                          onBlur={(e) => {
                            handleBlur(e);
                            inputBlurHandler(e);
                          }}
                        />
                      </Col>
                    </Row>
                  )}
                  {values.country === "Canada" && (
                    <Row className="mb-2">
                      <Col xs="12">
                        <label
                          className={
                            values.country === "Canada" ? "required" : ""
                          }
                          htmlFor="province"
                        >
                          Province:
                        </label>
                        <Field
                          name="province"
                          component={ReactstrapSelect}
                          inputprops={{
                            name: "province",
                            id: "province",
                            options: provincesArray,
                            defaultOption: "Select...",
                          }}
                          onChange={(e) => {
                            handleChange(e);
                            addressUpdateHandler(e);
                          }}
                          onFocus={inputFocusHandler}
                          onBlur={(e) => {
                            handleBlur(e);
                            inputBlurHandler(e);
                          }}
                        />
                      </Col>
                    </Row>
                  )}
                  <Row className="mb-2">
                    <Col xs="6">
                      <label className="required" htmlFor="city">
                        City:
                      </label>
                      <Field
                        name="city"
                        id="city"
                        maxlength="50"
                        component={ReactstrapInput}
                        onKeyUp={addressUpdateHandler}
                        onFocus={inputFocusHandler}
                        onBlur={(e) => {
                          handleBlur(e);
                          inputBlurHandler(e);
                        }}
                      />
                    </Col>
                    <Col xs="6">
                      <label
                        className={
                          values.country === "USA" ||
                          values.country === "Canada"
                            ? "required"
                            : ""
                        }
                        htmlFor="postalCode"
                      >
                        Zip/Postal Code:
                      </label>
                      <Field
                        name="postalCode"
                        id="postalCode"
                        maxlength="10"
                        component={ReactstrapInput}
                        onKeyUp={addressUpdateHandler}
                        onFocus={inputFocusHandler}
                        onBlur={(e) => {
                          handleBlur(e);
                          inputBlurHandler(e);
                        }}
                      />
                    </Col>
                  </Row>
                </div>
              )}
              {/* <DebugOutput
                errors={errors}
                values={FormHelpers.getValues(values)}
              /> */}
            </Container>
            <Button
              type="button"
              size="sm"
              color="primary"
              onClick={previousClickHandler}
              className="btn-secondary"
            >
              Previous
            </Button>{" "}
            <Button type="submit" size="sm" color="primary" className="float-end">
              Next
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default ReturnForm;
